<script>
import {
    modalState, hideModal
} from '@app/store/modalStore.svelte.js';
import {
    fade
} from 'svelte/transition';
import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
import { lockState } from "@app/store/lockStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import { styleState } from "@app/store/styleStore.svelte.js";
import { authState } from "@app/store/authStore.svelte.js";
import { toast } from "@zerodevx/svelte-toast";
import { preventDefault } from 'svelte/legacy';
import { Circle } from "svelte-loading-spinners";
import Swal from "sweetalert2";
import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
import { v4 as uuidv4 } from "uuid";
import { decode } from "html-entities";
import * as m from "@app/paraglide/messages.js";

/* Firebase */
import firebase from "@app/configs/firebase.js";
import db from "@app/configs/firestore.js";
import storage from "@app/configs/storage.js";

/* CC LIB */
import tcLib from '@cclib/lib/timecode.js'
import encode from "@cclib/functions/encode.js";
import defaults from "@cclib/lib/defaults.js";
import formats from "@cclib/lib/formats.js";
import frameRates from "@cclib/dict/frameRates.js";
import sccLib from "@cclib/dict/608.js";
import convertToPlainText from "@cclib/functions/quill/convertToPlainText.js";
import orderByTime from "@cclib/functions/eventGroups/orderByTime.js";
import removeHtmlEntities from "@cclib/functions/eventGroups/removeHtmlEntities.js";
import replaceTrialText from "@cclib/functions/eventGroups/replaceTrialText.js";
import wrapStyledTextWithSpan from "@cclib/functions/quill/wrapStyledTextWithSpan.js";

// State variables
let videoFile = $state(null);
let eventGroup = $state(
        getSelectedEventGroup([
            "subtitle",
            "translation",
            "audio description",
            "transcription",
        ])
);
let trgFrameRate = $state(projectState.frameRate);
let trgDropFrame = $state(projectState.dropFrame ? "true" : "false");
let processing = $state(false);
let progress = $state(0);
let uploadProgress = $state(0);
let jobId = $state("");
let customName = $state("");

// Pricing constants
const EMBED_BASE_COST = 200; // $2.00 in cents
const STORAGE_COST_PER_GB = 40; // $0.40 per GB in cents

// Computed pricing
let fileSizeGB = $derived(videoFile ? videoFile.size / (1024 * 1024 * 1024) : 0);
let billableGB = $derived(fileSizeGB < 1 ? 1 : Math.round(fileSizeGB)); // Minimum 1GB, round to nearest GB
let storageCost = $derived(billableGB * STORAGE_COST_PER_GB);
let totalCost = $derived(EMBED_BASE_COST + storageCost);

const userId = firebase.auth().currentUser?.uid;
const userEmail = firebase.auth().currentUser?.email;
const teamId = authState.team ? authState.team.id : null;
const homeRef = authState.team 
    ? db.collection("teams").doc(teamId).collection("jobs") 
    : db.collection("users").doc(userId).collection("jobs");
const storageRef = storage.ref();
const uploadBasePath = teamId ? `teams/${teamId}/embed/` : `users/${userId}/embed/`;

// Get valid event groups (subtitle, translation, transcription)
function getValidEventGroups() {
    return eventGroupState.filter(eg => 
        ["subtitle", "translation", "transcription", "audio description"].includes(eg.type)
    );
}

function getSelectedEventGroup(validTypes) {
    let evGroup = eventGroupState[projectState.selected];
    if (evGroup) {
        if (validTypes.indexOf(evGroup.type) > -1) {
            return evGroup.id;
        } else {
            return false;
        }
    } else {
        return false;
    }
}

// Handle file selection
function handleFileInput(event) {
    const file = event.currentTarget.files?.[0];
    if (file) {
        // Check file type
        videoFile = file;
        if (!customName) {
            customName = file.name.replace(/\.[^/.]+$/, "");
        }
    }
}

// Sanitize custom name to only include alphanumeric characters
function sanitizeCustomName(name) {
    return name.replace(/[^a-zA-Z0-9]/g, '');
}

// Start submission process
async function submitJob() {
    if (!videoFile) {
        toast.push(m.embed_captions_select_video_file(), { classes: ["toast-danger"] });
        return;
    }
    if (!eventGroup) {
        toast.push(m.embed_captions_select_caption_track(), { classes: ["toast-danger"] });
        return;
    }

    // Show payment confirmation
    const confirmResult = await Swal.fire({
        ...SWAL_BASE,
        title: m.embed_captions_confirm_title(),
        html: `
            <div class="text-start">
                <div class="row mb-2">
                    <div class="col">${m.embed_captions_confirm_processing_label()}</div>
                    <div class="col-auto"><strong>$${(EMBED_BASE_COST / 100).toFixed(2)}</strong></div>
                </div>
                <div class="row mb-2">
                    <div class="col">${m.embed_captions_confirm_storage_label({ gb: billableGB })}</div>
                    <div class="col-auto"><strong>$${(storageCost / 100).toFixed(2)}</strong></div>
                </div>
                <hr class="my-2">
                <div class="row mb-3">
                    <div class="col"><strong>${m.embed_captions_confirm_total_label()}</strong></div>
                    <div class="col-auto"><strong>$${(totalCost / 100).toFixed(2)}</strong></div>
                </div>
                <p class="text-secondary small mb-0">${m.embed_captions_confirm_storage_note()}</p>
            </div>
        `,
        icon: "info",
        showCancelButton: true,
        confirmButtonText: m.embed_captions_confirm_submit(),
        cancelButtonText: m.embed_captions_confirm_cancel(),
        customClass: SWAL_CLASSES.confirmCancel,
    });

    if (!confirmResult.isConfirmed) {
        return;
    }

    processing = true;
    progress = 10;
    
    try {
        trgFrameRate = parseFloat(trgFrameRate);
        if ([23.976,24,25,30,50,60].indexOf(trgFrameRate) > -1){
            trgDropFrame = false;
        }

        // Run automated checks
        const selectedEventGroupIndex = eventGroupState.findIndex(eg => eg.id === eventGroup);
        if (selectedEventGroupIndex === -1) {
            throw new Error(m.embed_captions_event_group_not_found());
        }
        
        let copyOfEventGroup = JSON.parse(JSON.stringify(eventGroupState[selectedEventGroupIndex]));
        copyOfEventGroup = orderByTime(copyOfEventGroup);
        copyOfEventGroup = removeHtmlEntities(copyOfEventGroup);
        
        progress = 20;
        
        // Perform automated checks
        const checkResult = await preExportCheck(copyOfEventGroup.events);
        if (checkResult.err) {
            cancelExport(selectedEventGroupIndex, checkResult.eventId);
            return;
        }
        
        progress = 30;
        
        // Apply trial text if in trial mode
        if (authState.status === "in_trial") {
            copyOfEventGroup = replaceTrialText(copyOfEventGroup);
        }
        
        copyOfEventGroup.events.forEach((event, index, events) => {
            events[index].text = wrapStyledTextWithSpan(event.text);
        });
        
        // Generate unique job ID
        jobId = uuidv4();
        
        // Convert event group to MCC
        let options = new defaults.options({
            profile: "macCaption",
            formatOptions: formats.profileMapping["MacCaption 608/708"].options.encode,
            frameRate: parseFloat(trgFrameRate),
            dropFrame: trgDropFrame === "true" ? true : false,
            target_profile: "macCaption",
            source_profile: "closedCaptionProject",
            incode: "00:00:00:00",
            window: {
                width: styleState.width,
                height: styleState.height,
                xPadding: styleState.xPadding,
                yPadding: styleState.yPadding
            }
        });
        
        progress = 40;
        
        // Encode to MCC
        const mccOutput = await encode(copyOfEventGroup, options);        
        progress = 50;
        
        // Register job in Firestore
        const sanitizedCustomName = sanitizeCustomName(customName || copyOfEventGroup.name);
        const jobInfo = {
            id: jobId,
            projectId: jobId,
            projectName: customName || copyOfEventGroup.name, // Keep display name with special chars
            submittedBy: userEmail,
            userId: userId,
            progress: 0,
            cost: totalCost,
            embedCost: EMBED_BASE_COST,
            storageCost: storageCost,
            fileSizeGB: fileSizeGB,
            billableGB: billableGB,
            type: "embed",
            statusMsg: m.embed_captions_status_awaiting_processing(),
            status: m.embed_captions_status_submitted(),
            createdOn: firebase.firestore.Timestamp.fromDate(new Date()),
            updatedOn: firebase.firestore.Timestamp.fromDate(new Date()),
            completedOn: null,
            deleted: false,
            customName: sanitizedCustomName, // Use sanitized name for storage
            mediaFile: videoFile.name,
            fileSize : videoFile.size,
            subtitleFile: `${jobId}.mcc`,
            subtitleFileType: "mcc",
            config: {
                frameRate: parseFloat(trgFrameRate),
                dropFrame: trgDropFrame === "true" ? true : false
            }
        };
        
        await homeRef.doc(jobInfo.id).set(jobInfo);
        progress = 60;
        
        // Upload MCC file to Firebase Storage
        const mccRef = storageRef.child(`${uploadBasePath}${jobId}.mcc`);
        await mccRef.putString(mccOutput);
        
        progress = 70;
        
        // Upload video file to Firebase Storage
        const videoRef = storageRef.child(`${uploadBasePath}${jobId}.${videoFile.name.split('.').pop()}`);
        const uploadTask = videoRef.put(videoFile);
        
        uploadTask.on('state_changed', 
            (snapshot) => {
                uploadProgress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
                progress = 70 + (uploadProgress * 0.2); // Scale upload progress to fit into our overall progress
            },
            (error) => {
                console.error("Upload error:", error);
                toast.push(m.embed_captions_upload_failed({ error: error.message }), {
                    classes: ["toast-danger"]
                });
                
                // Update job status to failed
                homeRef.doc(jobId).update({
                    progress: 100,
                    statusMsg: m.embed_captions_upload_failed_status({ error: error.code }),
                    status: m.embed_captions_status_failed(),
                    completedOn: firebase.firestore.Timestamp.fromDate(new Date()),
                    updatedOn: firebase.firestore.Timestamp.fromDate(new Date())
                });
                
                processing = false;
            },
            async () => {
                // Upload complete
                progress = 100;
                
                await Swal.fire({
                    ...SWAL_BASE,
                    title: m.embed_captions_submitted_title(),
                    text: m.embed_captions_submitted_text(),
                    icon: "success",
                    confirmButtonText: m.embed_captions_ok(),
                    customClass: SWAL_CLASSES.ok,
                });
                
                toast.push(m.embed_captions_submitted_toast(), {
                    classes: ["toast-success"]
                });
                
                processing = false;
                hideModal();
            }
        );
        
    } catch (error) {
        console.error("Error submitting job:", error);
        toast.push(m.embed_captions_submit_failed({ error: error.message }), {
            classes: ["toast-danger"]
        });
        processing = false;
    }
}

// Helper function to find first illegal character in event text
function findIllegalChar(text) {
    const plainText = convertToPlainText(text, "");
    for (const char of plainText) {
        // Skip space characters (charCode 32)
        if (char.charCodeAt(0) === 32) continue;
        if (sccLib.valid.indexOf(char) === -1) {
            return char;
        }
    }
    return null;
}

// Helper function to get line info (character count per line)
function getLineInfo(text) {
    const plainText = convertToPlainText(text);
    const lines = [];
    plainText.split("\n").forEach((line) => {
        lines.push(decode(line).length);
    });
    return lines;
}

// 608 caption constraints
const MAX_LINES = 4;
const MAX_CHARS_PER_LINE = 32;

async function preExportCheck(events) {
    let ignoreAll = false;
    const ignoreRemaining = () => {
        ignoreAll = true;
    };
    
    for (let i = 0; i < events.length; i++) {
        if (!ignoreAll) {
            if (events[i].text) {
                // Check for illegal characters first - this cannot be ignored
                const illegalChar = findIllegalChar(events[i].text);
                if (illegalChar) {
                    await Swal.fire({
                        ...SWAL_BASE,
                        titleText: m.embed_captions_illegal_char_title(),
                        text: m.embed_captions_illegal_char_text({
                            index: i + 1,
                            char: illegalChar
                        }),
                        icon: "error",
                        confirmButtonText: m.embed_captions_ok(),
                        allowOutsideClick: false,
                        allowEscapeKey: false,
                        customClass: SWAL_CLASSES.dangerOk,
                    });
                    return { err: true, eventId: i };
                }

                // Check line count - max 4 lines for 608 captions
                const lineInfo = getLineInfo(events[i].text);
                if (lineInfo.length > MAX_LINES) {
                    await Swal.fire({
                        ...SWAL_BASE,
                        titleText: m.embed_captions_too_many_lines_title(),
                        text: m.embed_captions_too_many_lines_text({
                            index: i + 1,
                            lines: lineInfo.length,
                            maxLines: MAX_LINES
                        }),
                        icon: "error",
                        confirmButtonText: m.embed_captions_ok(),
                        allowOutsideClick: false,
                        allowEscapeKey: false,
                        customClass: SWAL_CLASSES.dangerOk,
                    });
                    return { err: true, eventId: i };
                }

                // Check characters per line - max 32 characters for 608 captions

                // console.log(lineInfo);
                const longLineIndex = lineInfo.findIndex(charCount => charCount > MAX_CHARS_PER_LINE);
                // console.log("longLineIndex:", longLineIndex);
                if (longLineIndex !== -1) {
                    await Swal.fire({
                        ...SWAL_BASE,
                        titleText: m.embed_captions_line_too_long_title(),
                        text: m.embed_captions_line_too_long_text({
                            index: i + 1,
                            line: longLineIndex + 1,
                            count: lineInfo[longLineIndex],
                            maxChars: MAX_CHARS_PER_LINE
                        }),
                        icon: "error",
                        confirmButtonText: m.embed_captions_ok(),
                        allowOutsideClick: false,
                        allowEscapeKey: false,
                        customClass: SWAL_CLASSES.dangerOk,
                    });
                    return { err: true, eventId: i };
                }

                if (isNaN(events[i].start)) {
                    if (await alertUser(m.embed_captions_event_missing_start({ index: i + 1 }), ignoreRemaining)) {
                        return { err: true, eventId: i };
                    }
                } else if (isNaN(events[i].end)) {
                    if (await alertUser(m.embed_captions_event_missing_end({ index: i + 1 }), ignoreRemaining)) {
                        return { err: true, eventId: i };
                    }
                } else if (events[i].start === events[i].end) {
                    if (await alertUser(m.embed_captions_event_same_time({ index: i + 1 }), ignoreRemaining)) {
                        return { err: true, eventId: i };
                    }
                } else if (events[i].start > events[i].end) {
                    if (await alertUser(m.embed_captions_event_start_after_end({ index: i + 1 }), ignoreRemaining)) {
                        return { err: true, eventId: i };
                    }
                } else if (i > 0 && events[i].start < events[i - 1].end) {
                    /* Adding check in case overlap is less than a frame */
                    if (events[i - 1].end - events[i].start < 1 / parseFloat(trgFrameRate)) {
                        events[i].start = events[i - 1].end;
                    } else if (await alertUser(m.embed_captions_event_overlap({ index: i + 1 }), ignoreRemaining)) {
                        return { err: true, eventId: i };
                    }
                }
            } else {
                if (await alertUser(m.embed_captions_event_missing_text({ index: i + 1 }), ignoreRemaining)) {
                    return { err: true, eventId: i };
                }
            }
        }
    }

    return { err: false };
}

function alertUser(msg, onIgnoreAll) {
    let response = Swal.fire({
        ...SWAL_BASE,
        titleText: m.embed_captions_precheck_title(),
        text: msg,
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: m.embed_captions_precheck_abort(),
        denyButtonText: m.embed_captions_precheck_ignore(),
        cancelButtonText: m.embed_captions_precheck_ignore_all(),
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: SWAL_CLASSES.precheck,
    }).then((result) => {
        if (result.isConfirmed) {
            return true;
        } else if (result.isDenied) {
            return false;
        } else if (result.isDismissed) {
            onIgnoreAll();
            return false;
        }
    });

    return response;
}

function cancelExport(eventGroupId, eventId) {
    toast.push(m.embed_captions_aborted_toast(), {
        classes: ["toast-warning"],
    });

    projectState.selected = eventGroupId;
    eventGroupState[projectState.selected].selected = [eventId];   

    setTimeout(() => {
        const selectedIndex = eventGroupState[projectState.selected].selected[0];
        window.dispatchEvent(new CustomEvent('scrollToVirtualListItem', {
            detail: { index: selectedIndex }
        }));

        if (lockState.video && player){
            player.currentTime = eventGroupState[projectState.selected].events[selectedIndex].start;
        }
    }, 50);

    processing = false;
    hideModal();
}
</script>
<div
    transition:fade={{ duration: 100 }}
    class="modal {modalState.selected === 'embedCaptionsExport'
        ? 'show d-block'
        : ''}"
    role="dialog"
    aria-modal="true"
    tabindex="-1"
    id="ExportModal">
    <div class="modal-dialog modal-lg modal-dialog-centered" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title">{m.embed_captions_title()}</h4>
                <button
                    type="button"
                    class="btn-close"
                    aria-label={m.embed_captions_close_label()}
                    onclick={hideModal}>
                </button>
            </div>
            <div class="modal-body">
                <p><i class="bi bi-info-circle-fill"></i> {m.embed_captions_intro()}</p>
                <div class="p-3 bg-dark rounded my-3">
                    <p class="small text-light m-0">
                        {m.embed_captions_storage_notice_prefix()} <strong>{m.embed_captions_storage_notice_strong()}</strong>
                    </p>
                </div>
                

                <form onsubmit={preventDefault(() => submitJob())}>
                    <div class="card shadow-sm mb-4">
                        <div class="card-header bg-light">
                            <h5 class="card-title mb-0">{m.embed_captions_media_caption_selection()}</h5>
                        </div>
                        <div class="card-body">
                            <!-- Video File Upload -->
                            <div class="mb-3">
                                <label for="videoFileInput" class="form-label fw-bold">
                                    {m.embed_captions_video_file_label()}
                                </label>
                                <div class="input-group">
                                    <input 
                                        type="file" 
                                        class="form-control" 
                                        id="videoFileInput"
                                        accept=".mov,.mp4,.gxf,.mpeg,.mxf,.mpg" 
                                        onchange={handleFileInput}
                                        disabled={processing}
                                    />
                                </div>
                                <div class="form-text">{m.embed_captions_video_file_help()}</div>
                                {#if videoFile}
                                    <div class="mt-2 d-flex align-items-center">
                                        <span class="badge bg-success me-2">
                                            <i class="bi bi-check-lg"></i>
                                        </span>
                                        <small class="text-truncate">{videoFile.name}</small>
                                        <small class="text-muted ms-2">
                                            {m.embed_captions_file_size_gb({ size: fileSizeGB.toFixed(2) })}
                                        </small>
                                    </div>
                                {/if}
                            </div>

                            <!-- Custom Name -->
                            <div class="mb-3">
                                <label for="customNameInput" class="form-label fw-bold">
                                    {m.embed_captions_custom_name_label()}
                                </label>
                                <input 
                                    type="text" 
                                    class="form-control" 
                                    id="customNameInput" 
                                    placeholder={m.embed_captions_custom_name_placeholder()}
                                    bind:value={customName}
                                    disabled={processing}
                                />
                            </div>

                            <!-- Event Group Selection -->
                            <div class="mb-3">
                                <label for="eventGroupSelect" class="form-label fw-bold">
                                    {m.embed_captions_caption_track_label()}
                                </label>
                                <select 
                                    class="form-select" 
                                    id="eventGroupSelect" 
                                    bind:value={eventGroup}
                                    disabled={processing}
                                >
                                    <option value={null}>{m.embed_captions_caption_track_placeholder()}</option>
                                    {#each getValidEventGroups() as eg (eg.id)}
                                        <option value={eg.id}>{eg.name}</option>
                                    {/each}
                                </select>
                                <div class="form-text">{m.embed_captions_caption_track_help()}</div>
                            </div>

                            <div class="row">                               
                                <!-- Frame Rate -->
                                <div class="mb-3 col-md-4">
                                    <label for="frameRateSelect" class="form-label fw-bold">
                                        {m.embed_captions_frame_rate_label()}
                                    </label>
                                    <select 
                                        class="form-select" 
                                        id="frameRateSelect" 
                                        bind:value={trgFrameRate}
                                        disabled={processing}
                                        onchange={() => {
                                            if (parseFloat(trgFrameRate) === 29.97 || parseFloat(trgFrameRate) === 59.94) {
                                                trgDropFrame = "true";
                                            } else {
                                                trgDropFrame = "false";
                                            }
                                        }}
                                    >
                                        {#each frameRates.frameRates as frameRate (frameRate)}
                                            <option value={frameRate}>{frameRate}</option>
                                        {/each}
                                    </select>
                                    <div class="form-text">{m.embed_captions_frame_rate_help()}</div>
                                </div>

                                <!-- Drop Frame (only shown for 29.97 and 59.94) -->
                                {#if parseFloat(trgFrameRate) === 29.97 || parseFloat(trgFrameRate) === 59.94}
                                    <div class="mb-3 col-md-4">
                                        <label for="dropFrameSelect" class="form-label fw-bold">
                                            {m.embed_captions_drop_frame_label()}
                                        </label>
                                        <select 
                                            class="form-select" 
                                            id="dropFrameSelect" 
                                            bind:value={trgDropFrame}
                                            disabled={processing}
                                        >
                                            <option value="true">{m.embed_captions_yes()}</option>
                                            <option value="false">{m.embed_captions_no()}</option>
                                        </select>
                                        <div class="form-text">{m.embed_captions_drop_frame_help()}</div>
                                    </div>
                                {/if}
                            </div>
                        </div>
                    </div>

                    {#if videoFile}
                        <div class="card shadow-sm mb-4">
                            <div class="card-header bg-light">
                                <h5 class="card-title mb-0">{m.embed_captions_pricing_summary()}</h5>
                            </div>
                            <div class="card-body">
                                <div class="row mb-2">
                                    <div class="col">
                                        <span class="fw-medium">{m.embed_captions_pricing_processing_label()}</span>
                                        <div class="text-muted small">{m.embed_captions_pricing_processing_help()}</div>
                                    </div>
                                    <div class="col-auto">
                                        <span class="fw-bold">${(EMBED_BASE_COST / 100).toFixed(2)}</span>
                                    </div>
                                </div>
                                <div class="row mb-2">
                                    <div class="col">
                                        <span class="fw-medium">{m.embed_captions_pricing_storage_label()}</span>
                                        <div class="text-muted small">{m.embed_captions_pricing_storage_help({ gb: billableGB })}</div>
                                    </div>
                                    <div class="col-auto">
                                        <span class="fw-bold">${(storageCost / 100).toFixed(2)}</span>
                                    </div>
                                </div>
                                <hr class="my-2">
                                <div class="row">
                                    <div class="col">
                                        <span class="fw-bold fs-5">{m.embed_captions_pricing_total_label()}</span>
                                    </div>
                                    <div class="col-auto">
                                        <span class="fw-bold fs-5 text-primary">${(totalCost / 100).toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    {/if}

                    {#if processing}
                        <div class="card shadow-sm mb-3">
                            <div class="card-body">
                                <h6 class="card-title d-flex align-items-center">
                                    <div class="spinner-border spinner-border-sm text-primary me-2" role="status">
                                        <span class="visually-hidden">{m.embed_captions_processing_label()}</span>
                                    </div>
                                    {m.embed_captions_processing_job()}
                                </h6>
                                <div class="mb-1">
                                    <div class="d-flex justify-content-between align-items-center mb-1">
                                        <span class="small">{m.embed_captions_overall_progress()}</span>
                                        <span class="badge bg-primary">{Math.round(progress)}%</span>
                                    </div>
                                    <div class="progress" style="height: 8px;">
                                        <div class="progress-bar progress-bar-striped progress-bar-animated" 
                                            role="progressbar" 
                                            style="width: {progress}%" 
                                            aria-valuenow={progress} 
                                            aria-valuemin="0" 
                                            aria-valuemax="100">
                                        </div>
                                    </div>
                                </div>
                                {#if uploadProgress > 0}
                                    <div>
                                        <div class="d-flex justify-content-between align-items-center mb-1">
                                            <span class="small">{m.embed_captions_video_upload()}</span>
                                            <span class="badge bg-primary">{uploadProgress}%</span>
                                        </div>
                                        <div class="progress" style="height: 5px;">
                                            <div class="progress-bar bg-primary" 
                                                role="progressbar" 
                                                style="width: {uploadProgress}%" 
                                                aria-valuenow={uploadProgress} 
                                                aria-valuemin="0" 
                                                aria-valuemax="100">
                                            </div>
                                        </div>
                                    </div>
                                {/if}
                            </div>
                        </div>
                    {/if}

                    <div class="alert alert-warning small">
                        <i class="bi bi-exclamation-triangle-fill me-2"></i>
                        <strong>{m.embed_captions_important_label()}</strong> {m.embed_captions_important_text()} 
                        {#if videoFile}
                            <strong>{m.embed_captions_important_total({ total: (totalCost / 100).toFixed(2) })}</strong>
                        {:else}
                            <strong>{m.embed_captions_important_pricing_pending()}</strong>
                        {/if}
                    </div>
                </form>
            </div>
            <div class="modal-footer">
                <button
                    type="button"
                    class="btn btn-secondary"
                    onclick={hideModal}
                    disabled={processing}>{m.embed_captions_close_button()}</button>
                <button 
                    type="button"
                    class="btn btn-primary"
                    onclick={submitJob}
                    disabled={!videoFile || !eventGroup || processing}>
                    {#if processing}
                        <Circle size="16" color="#ffffff" unit="px" duration="1s"/>
                        <span class="ms-2">{m.embed_captions_processing_label()}</span>
                    {:else}
                        <i class="bi bi-cloud-upload me-2"></i>
                        {m.embed_captions_submit_button()}
                    {/if}
                </button>
            </div>
        </div>
    </div>
</div>

<style>
.progress {
    border-radius: 1rem;
    background-color: #e9ecef;
}

.text-truncate {
    max-width: 300px;
    display: inline-block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: middle;
}
</style>
